import React, { useState } from 'react';
import {
    Typography,
    Box,
    Link,
    Stack,
    Collapse
} from '@mui/material';
import {
    Warning as WarningIcon,
    CheckCircle as CheckIcon
} from '@mui/icons-material';
import SourceButton from './SourceButton';
import _ from 'lodash';
import { Chip } from '@mui/material';

const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX for 10 digits or +1 (XXX) XXX-XXXX for 11 digits
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    // Return original if it doesn't match expected patterns
    return phoneNumber;
};

const StatusItem = ({ type, text }) => {
    const isWarning = type === 'warning';
    const isSuccess = type === 'success';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isWarning && (
                <WarningIcon sx={{ fontSize: 16, color: '#EF4444' }} />
            )}
            {isSuccess && (
                <CheckIcon sx={{ fontSize: 16, color: '#10B981' }} />
            )}
            <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.875rem' }}>
                {text}
            </Typography>
        </Box>
    );
};

const PhoneItem = ({ phone, isInputData }) => {
    const { value, assessments = [], source = [] } = phone;
    const [expanded, setExpanded] = useState(false);

    const handleItemClick = () => {
        setExpanded(!expanded);
    };

    const handlePhoneClick = (e) => {
        e.stopPropagation(); // Prevent the item click handler from firing
        // Allow tel: link to work normally
    };

    return (
        <Box sx={{
            mb: 1,
            border: '1px solid #E2E6F0',
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(242, 243, 251, 0.4)',
            }
        }}>
            {/* Main content area */}
            <Box
                onClick={handleItemClick}
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}>
                {/* Left section with phone info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Phone number */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 400, fontSize: '1rem', letterSpacing: '0.3px' }}>
                            <Link
                                href={`tel:${value}`}
                                underline="hover"
                                onClick={handlePhoneClick}
                                sx={{
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                {formatPhoneNumber(value)}
                            </Link>
                        </Typography>
                        {isInputData && (
                            <Chip label="Input Data" size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontSize: '0.75rem' }} />
                        )}
                    </Box>
                </Box>

                {/* Right section with source */}
                <Box
                    sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <SourceButton sources={source} />
                </Box>
            </Box>

            {/* Collapsible Status items - Full width */}
            <Collapse in={expanded} timeout={300}>
                <Box sx={{
                    borderTop: '1px solid #e0e0e0',
                    px: 2,
                    pb: 2,
                    pt: 1
                }}>
                    <Stack spacing={0.5}>
                        {_.map(assessments, (assessment, index) => {
                            if (!assessment.ui_string || assessment.ui_string === "None") return null;

                            const score = parseFloat(assessment.score);
                            const statusType = score > 1.0 ? 'warning' : 'success';

                            return (
                                <StatusItem
                                    key={index}
                                    type={statusType}
                                    text={assessment.ui_string}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </Collapse>
        </Box>
    );
};

export default PhoneItem;